import { motion } from "framer-motion";

// Survey-themed decorative vectors (aria-hidden). Use currentColor for tinting.
const draw = (d = 0) => ({ initial: { pathLength: 0 }, whileInView: { pathLength: 1 }, viewport: { once: false, amount: 0.05 }, transition: { duration: 2.4, delay: d, ease: "easeInOut" } });

export const Contours = ({ className = "", animate = true }) => (
  <svg viewBox="0 0 800 600" className={className} fill="none" stroke="currentColor" aria-hidden="true">
    {Array.from({ length: 11 }).map((_, i) => {
      const r = 40 + i * 26;
      const d = `M ${400 - r * 1.3} 300 C ${400 - r * 1.2} ${300 - r * 0.9}, ${400 + r * 0.4} ${300 - r * 1.1}, ${400 + r * 1.25} ${300 - r * 0.3} S ${400 + r * 0.5} ${300 + r * 1.05}, ${400 - r * 0.2} ${300 + r * 0.8} S ${400 - r * 1.4} ${300 + r * 0.4}, ${400 - r * 1.3} 300 Z`;
      return animate ? <motion.path key={i} d={d} strokeWidth={i % 5 === 0 ? 1.4 : 0.7} {...draw(i * 0.08)} /> : <path key={i} d={d} strokeWidth={i % 5 === 0 ? 1.4 : 0.7} />;
    })}
  </svg>
);

export const Tripod = ({ className = "" }) => (
  <svg viewBox="0 0 200 300" className={className} fill="none" stroke="currentColor" strokeWidth="1.2" aria-hidden="true">
    <rect x="80" y="20" width="40" height="34" /><circle cx="100" cy="37" r="9" /><path d="M100 28v18M91 37h18" />
    <rect x="70" y="54" width="60" height="10" /><path d="M100 64v24M100 88 40 280M100 88l60 192M100 88v192" />
    <path d="M60 220h80" strokeDasharray="3 4" /><path d="M120 37h40" strokeDasharray="2 3" />
    <text x="128" y="16" fontSize="9" fill="currentColor" stroke="none" fontFamily="JetBrains Mono">TS-01</text>
  </svg>
);

export const Parcels = ({ className = "" }) => (
  <svg viewBox="0 0 600 400" className={className} fill="none" stroke="currentColor" aria-hidden="true">
    <motion.path d="M40 60 L260 40 L300 200 L60 230 Z" strokeWidth="1.2" {...draw()} />
    <motion.path d="M260 40 L520 70 L540 240 L300 200" strokeWidth="1.2" {...draw(0.3)} />
    <motion.path d="M60 230 L300 200 L320 360 L80 370 Z" strokeWidth="1.2" {...draw(0.6)} />
    <path d="M300 200 L540 240 L560 380 L320 360" strokeWidth="1.2" strokeDasharray="6 5" />
    {[[40, 60], [260, 40], [300, 200], [60, 230], [520, 70], [540, 240], [320, 360], [80, 370]].map(([x, y], i) => (
      <g key={i}><circle cx={x} cy={y} r="4" /><path d={`M${x - 8} ${y}h16M${x} ${y - 8}v16`} strokeWidth=".6" /></g>
    ))}
    <text x="120" y="140" fontSize="11" fill="currentColor" stroke="none" fontFamily="JetBrains Mono">LOT 14</text>
    <text x="380" y="150" fontSize="11" fill="currentColor" stroke="none" fontFamily="JetBrains Mono">LOT 15</text>
    <text x="150" y="52" fontSize="9" fill="currentColor" stroke="none" fontFamily="JetBrains Mono" transform="rotate(-5 150 52)">N 84°48'20" E 220.94'</text>
  </svg>
);

export const Axes = ({ className = "" }) => (
  <svg viewBox="0 0 500 500" className={className} fill="none" stroke="currentColor" aria-hidden="true">
    <path d="M40 460 V40 M40 460 H460" strokeWidth="1.2" /><path d="M40 40l-5 10h10zM460 460l-10-5v10z" fill="currentColor" />
    {Array.from({ length: 9 }).map((_, i) => <path key={i} d={`M${40 + (i + 1) * 46} 456v8M36 ${460 - (i + 1) * 46}h8`} strokeWidth=".8" />)}
    <text x="20" y="30" fontSize="12" fill="currentColor" stroke="none" fontFamily="JetBrains Mono">N</text>
    <text x="468" y="474" fontSize="12" fill="currentColor" stroke="none" fontFamily="JetBrains Mono">E</text>
    {[[150, 300], [240, 210], [330, 260], [400, 130]].map(([x, y], i) => (
      <g key={i}><path d={`M${x - 7} ${y - 7}l14 14M${x + 7} ${y - 7}l-14 14`} strokeWidth="1" />
        <text x={x + 10} y={y - 8} fontSize="9" fill="currentColor" stroke="none" fontFamily="JetBrains Mono">P{100 + i}</text></g>
    ))}
    <motion.path d="M150 300 L240 210 L330 260 L400 130" strokeWidth="1.4" {...draw(0.2)} />
  </svg>
);

export const Section = ({ className = "" }) => (
  <svg viewBox="0 0 800 260" className={className} fill="none" stroke="currentColor" aria-hidden="true">
    <motion.path d="M0 170 C 80 150, 140 120, 220 130 S 360 190, 440 160 S 600 80, 680 100 S 760 140, 800 130" strokeWidth="1.4" {...draw()} />
    <path d="M0 200 H800" strokeDasharray="4 6" strokeWidth=".8" />
    {Array.from({ length: 17 }).map((_, i) => <path key={i} d={`M${i * 50} 200v8`} strokeWidth=".8" />)}
    {[[220, 130, "102.40"], [440, 160, "99.85"], [680, 100, "106.12"]].map(([x, y, t]) => (
      <g key={t}><path d={`M${x} ${y}v-30`} strokeWidth=".6" /><path d={`M${x - 6} ${y - 30}h12l-6 8z`} /><text x={x + 8} y={y - 32} fontSize="10" fill="currentColor" stroke="none" fontFamily="JetBrains Mono">{t}</text></g>
    ))}
  </svg>
);

export const GnssMarker = ({ className = "" }) => (
  <svg viewBox="0 0 120 120" className={className} fill="none" stroke="currentColor" aria-hidden="true">
    <circle cx="60" cy="60" r="44" strokeDasharray="3 5" /><circle cx="60" cy="60" r="26" /><circle cx="60" cy="60" r="4" fill="currentColor" />
    <path d="M60 4v30M60 86v30M4 60h30M86 60h30" />
  </svg>
);
